import * as THREE from 'three';
import { Drivetrain } from './Drivetrain';

export class Turbine {
  public group: THREE.Group;
  public nacelleGroup: THREE.Group;
  public rotorGroup: THREE.Group;
  public drivetrain: Drivetrain;

  // External interactive meshes
  public nacelleMesh!: THREE.Mesh;
  public nacelleShellGroup: THREE.Group;
  private shellLeftMesh!: THREE.Mesh;
  private shellRightMesh!: THREE.Mesh;
  private shellMat!: THREE.MeshStandardMaterial;

  // Red beacon light
  private beaconLight!: THREE.PointLight;
  private beaconMesh!: THREE.Mesh;
  private beaconTimer: number = 0;

  // State
  private isPlaying: boolean = true;
  private rotorAngle: number = 0;
  private rotorRpm: number = 10; // RPM
  private targetRpm: number = 10;
  private yawAngle: number = 0;
  private shellOpenProgress: number = 0;
  private targetShellOpen: number = 0;

  // Hub height in world units
  public static readonly HUB_HEIGHT: number = 88;

  constructor() {
    this.group = new THREE.Group();
    this.nacelleGroup = new THREE.Group();
    this.rotorGroup = new THREE.Group();
    this.nacelleShellGroup = new THREE.Group();
    this.drivetrain = new Drivetrain();

    this.buildFoundation();
    this.buildTower();
    this.buildNacelle();
    this.buildRotor();
    this.buildBeacon();

    // Position nacelle at hub height
    this.nacelleGroup.position.set(0, Turbine.HUB_HEIGHT, 0);
    this.group.add(this.nacelleGroup);
  }

  private buildFoundation() {
    // Offshore transition piece & yellow platform
    const foundationMat = new THREE.MeshStandardMaterial({
      color: 0xeab308, // Marine safety yellow
      roughness: 0.45,
      metalness: 0.3
    });

    const submergedMat = new THREE.MeshStandardMaterial({
      color: 0x334155,
      roughness: 0.7,
      metalness: 0.5
    });

    // Underwater monopile / jacket
    const pileGeo = new THREE.CylinderGeometry(4.0, 4.2, 35, 32);
    const pileMesh = new THREE.Mesh(pileGeo, submergedMat);
    pileMesh.position.set(0, -10, 0);
    this.group.add(pileMesh);

    // Transition piece (above water line to tower base)
    const tpGeo = new THREE.CylinderGeometry(3.6, 3.9, 16, 32);
    const tpMesh = new THREE.Mesh(tpGeo, foundationMat);
    tpMesh.position.set(0, 7, 0);
    this.group.add(tpMesh);

    // Maintenance platform & railing
    const platformGeo = new THREE.CylinderGeometry(4.6, 4.6, 0.4, 32);
    const platform = new THREE.Mesh(platformGeo, foundationMat);
    platform.position.set(0, 14.8, 0);
    this.group.add(platform);

    // Platform railing
    const railGeo = new THREE.TorusGeometry(4.5, 0.08, 8, 32);
    railGeo.rotateX(Math.PI / 2);
    const rail = new THREE.Mesh(railGeo, foundationMat);
    rail.position.set(0, 15.9, 0);
    this.group.add(rail);

    // Boat landing ladder
    const ladderGeo = new THREE.BoxGeometry(0.8, 16, 0.2);
    const ladder = new THREE.Mesh(ladderGeo, foundationMat);
    ladder.position.set(0, 6, 4.2);
    this.group.add(ladder);
  }

  private buildTower() {
    // Tapered tower cylinder
    const towerMat = new THREE.MeshStandardMaterial({
      color: 0xf1f5f9, // Off white
      roughness: 0.35,
      metalness: 0.2
    });

    const towerHeight = Turbine.HUB_HEIGHT - 15;
    const towerGeo = new THREE.CylinderGeometry(2.3, 3.5, towerHeight, 36);
    const towerMesh = new THREE.Mesh(towerGeo, towerMat);
    towerMesh.position.set(0, 15 + towerHeight / 2, 0);
    towerMesh.castShadow = true;
    towerMesh.receiveShadow = true;
    this.group.add(towerMesh);
  }

  private buildNacelle() {
    // Shell material
    this.shellMat = new THREE.MeshStandardMaterial({
      color: 0xf8fafc,
      roughness: 0.3,
      metalness: 0.25,
      transparent: true,
      opacity: 0.98,
      side: THREE.DoubleSide
    });

    // Outer shell shape (streamlined wind turbine nacelle)
    // Left shell half
    const shellShape = new THREE.BoxGeometry(2.2, 4.2, 14.5);
    this.shellLeftMesh = new THREE.Mesh(shellShape, this.shellMat);
    this.shellLeftMesh.position.set(-1.15, 0.5, -1.8);
    this.shellLeftMesh.name = 'nacelleShellLeft';

    // Right shell half
    this.shellRightMesh = new THREE.Mesh(shellShape, this.shellMat);
    this.shellRightMesh.position.set(1.15, 0.5, -1.8);
    this.shellRightMesh.name = 'nacelleShellRight';

    this.nacelleShellGroup.add(this.shellLeftMesh);
    this.nacelleShellGroup.add(this.shellRightMesh);
    this.nacelleGroup.add(this.nacelleShellGroup);

    // Nacelle click detection dummy box
    const nacelleHitGeo = new THREE.BoxGeometry(5.0, 4.8, 15.5);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    this.nacelleMesh = new THREE.Mesh(nacelleHitGeo, hitMat);
    this.nacelleMesh.position.set(0, 0.5, -1.8);
    this.nacelleMesh.name = 'nacelleOuter';
    this.nacelleGroup.add(this.nacelleMesh);

    // Mount Drivetrain inside nacelle
    this.nacelleGroup.add(this.drivetrain.group);
  }

  private buildRotor() {
    // Hub (nose cone)
    const hubMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0,
      roughness: 0.35,
      metalness: 0.3
    });

    const hubGeo = new THREE.ConeGeometry(2.1, 4.4, 32);
    hubGeo.rotateX(Math.PI / 2);
    const hubMesh = new THREE.Mesh(hubGeo, hubMat);
    hubMesh.position.set(0, 0, 5.2);
    this.rotorGroup.add(hubMesh);

    // Hub cylinder base
    const hubBaseGeo = new THREE.CylinderGeometry(2.0, 1.8, 1.6, 32);
    hubBaseGeo.rotateX(Math.PI / 2);
    const hubBase = new THREE.Mesh(hubBaseGeo, hubMat);
    hubBase.position.set(0, 0, 3.8);
    this.rotorGroup.add(hubBase);

    // 3 Aerodynamic Blades (120 degrees apart)
    for (let i = 0; i < 3; i++) {
      const angle = (i * Math.PI * 2) / 3;
      const bladeGroup = this.createBlade();
      bladeGroup.rotation.z = angle;
      this.rotorGroup.add(bladeGroup);
    }

    this.rotorGroup.position.set(0, 0.45, 0.6);
    this.nacelleGroup.add(this.rotorGroup);
  }

  private createBlade(): THREE.Group {
    const bladeGroup = new THREE.Group();

    const bladeMat = new THREE.MeshStandardMaterial({
      color: 0xf1f5f9,
      roughness: 0.3,
      metalness: 0.15
    });

    const redTipMat = new THREE.MeshStandardMaterial({
      color: 0xdc2626, // Offshore safety red stripes
      roughness: 0.35
    });

    // Blade Root (Cylindrical transition)
    const rootGeo = new THREE.CylinderGeometry(0.75, 0.95, 4.0, 24);
    const rootMesh = new THREE.Mesh(rootGeo, bladeMat);
    rootMesh.position.set(0, 3.8, 4.6);
    bladeGroup.add(rootMesh);

    // Main Airfoil Body (Extending from 5.8m to 48m, length ~42m)
    // Tapered, twisted shape approximated with clean segmented loft
    const bodyLength = 38;
    const bodyGeo = new THREE.ConeGeometry(1.6, bodyLength, 12);
    // Flatten cone to resemble aerodynamic airfoil
    bodyGeo.scale(0.35, 1.0, 1.0);
    const bodyMesh = new THREE.Mesh(bodyGeo, bladeMat);
    bodyMesh.position.set(0, 5.8 + bodyLength / 2, 4.6);
    bladeGroup.add(bodyMesh);

    // Aerodynamic tip section with red warning paint
    const tipLength = 10;
    const tipGeo = new THREE.ConeGeometry(0.7, tipLength, 12);
    tipGeo.scale(0.25, 1.0, 1.0);
    const tipMesh = new THREE.Mesh(tipGeo, redTipMat);
    tipMesh.position.set(0, 5.8 + bodyLength + tipLength / 2, 4.6);
    bladeGroup.add(tipMesh);

    return bladeGroup;
  }

  private buildBeacon() {
    // Red obstruction light on top of nacelle
    this.beaconLight = new THREE.PointLight(0xff0000, 0, 45);
    this.beaconLight.position.set(0, 3.1, -5.5);
    this.nacelleGroup.add(this.beaconLight);

    const beaconGeo = new THREE.SphereGeometry(0.22, 16, 16);
    const beaconMat = new THREE.MeshStandardMaterial({
      color: 0xef4444,
      emissive: 0xff0000,
      emissiveIntensity: 0.2
    });
    this.beaconMesh = new THREE.Mesh(beaconGeo, beaconMat);
    this.beaconMesh.position.set(0, 3.1, -5.5);
    this.nacelleGroup.add(this.beaconMesh);
  }

  public update(delta: number) {
    // Update rotor rotation
    if (this.isPlaying) {
      // Smoothly approach target RPM
      this.rotorRpm += (this.targetRpm - this.rotorRpm) * Math.min(delta * 2.0, 1.0);
      const radPerSec = (this.rotorRpm * 2 * Math.PI) / 60;
      this.rotorAngle += radPerSec * delta;
      this.rotorGroup.rotation.z = this.rotorAngle;

      // Update internal drivetrain shafts
      this.drivetrain.update(this.rotorRpm, delta);
    }

    // Smooth shell opening/closing animation
    if (Math.abs(this.shellOpenProgress - this.targetShellOpen) > 0.001) {
      this.shellOpenProgress += (this.targetShellOpen - this.shellOpenProgress) * Math.min(delta * 4.0, 1.0);

      // Slide and rotate open shell halves
      const slideX = this.shellOpenProgress * 2.8;
      const liftY = this.shellOpenProgress * 1.5;
      const rotZ = this.shellOpenProgress * 0.35;

      this.shellLeftMesh.position.x = -1.15 - slideX;
      this.shellLeftMesh.position.y = 0.5 + liftY;
      this.shellLeftMesh.rotation.z = rotZ;

      this.shellRightMesh.position.x = 1.15 + slideX;
      this.shellRightMesh.position.y = 0.5 + liftY;
      this.shellRightMesh.rotation.z = -rotZ;

      // Make shell translucent when open
      this.shellMat.opacity = 0.98 - this.shellOpenProgress * 0.65;
      this.shellMat.transparent = this.shellOpenProgress > 0.01;
    }

    // Flash beacon light (1.2 second cycle: flash on for 0.3s)
    this.beaconTimer += delta;
    const cycle = this.beaconTimer % 1.4;
    const isFlash = cycle < 0.35;
    this.beaconLight.intensity = isFlash ? 3.5 : 0;
    const mat = this.beaconMesh.material as THREE.MeshStandardMaterial;
    mat.emissiveIntensity = isFlash ? 2.5 : 0.2;
  }

  public setPlaying(play: boolean) {
    this.isPlaying = play;
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }

  public setWindSpeed(speedMs: number) {
    // Typical offshore wind turbine curve:
    // Cut-in: ~3 m/s (rotor starts ~5 rpm)
    // Rated: ~11-12 m/s (rotor reaches ~12-14 rpm)
    // Cut-out: ~25 m/s (feathered/stops)
    if (speedMs < 2.5) {
      this.targetRpm = 1.0; // Idle drift
    } else if (speedMs > 25.0) {
      this.targetRpm = 0.0; // High wind cut-out safe stop
    } else {
      // Linear mapping for educational visualization: 3m/s -> 6 RPM, 12m/s -> 14 RPM
      const normalized = (speedMs - 2.5) / (12.0 - 2.5);
      this.targetRpm = Math.min(6.0 + normalized * 8.0, 15.0);
    }
  }

  public setYawDirection(degrees: number) {
    this.yawAngle = degrees;
    // Yaw nacelle around Y axis
    this.nacelleGroup.rotation.y = THREE.MathUtils.degToRad(degrees);
  }

  public setShellOpen(open: boolean) {
    this.targetShellOpen = open ? 1.0 : 0.0;
  }

  public getShellOpenProgress(): number {
    return this.shellOpenProgress;
  }
}
